import Spinner from "@/components/spinner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useCheckUrlStatus } from "@/hooks/check-url-status.hook";
import { useCreateResourceLinkMutation } from "@/hooks/create-resource-link.hook";
import { useEditionById } from "@/hooks/edition-by-id.hook";
import { useResourceLibrary } from "@/hooks/resource-library.hook";
import { cn } from "@/lib/utils";
import { currentEditionIdAtom } from "@/store/edition-id.store";
import { zodResolver } from "@hookform/resolvers/zod";
import { ExportSquare } from "iconsax-react";
import { useAtomValue } from "jotai";
import { AlertCircle, CheckCircle } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useDebounce } from "use-debounce";
import { z } from "zod";

/**
 * The data that is passed to the onOpenChange function when the drawer is closed
 * This contains the data related to the changed resource (link)
 */
export type OpenChangeData = null | {
  id: string;
};

export type LinkDrawerProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean, data: OpenChangeData) => void;
  addCurrentEditionTag?: boolean;
};

const urlSchema = z.string().url({ message: "Ingresa una URL válida" });

const formSchema = z.object({
  url: urlSchema,
  name: z
    .string()
    .min(3, { message: "El nombre debe tener al menos 3 caracteres" })
    .max(255, { message: "El nombre no puede tener más de 255 caracteres" }),
});

/**
 * Component for creating a new link/URL in the resource library.
 * It allows the user to provide a URL and a name for it.
 *
 * @param isOpen - Whether the drawer is open or not.
 * @param onOpenChange - Function to call when the drawer is closed or opened.
 * @param addCurrentEditionTag - Whether to add a tag for the current edition year to the link
 */
const LinkDrawer: FC<LinkDrawerProps> = ({
  isOpen,
  onOpenChange,
  addCurrentEditionTag,
}) => {
  const editionId = useAtomValue(currentEditionIdAtom);

  const { data: resourceLibrary } = useResourceLibrary();

  const { data: edition } = useEditionById({
    companyId: resourceLibrary?.owner.id || "",
    editionId,
  });

  const [isSaveAnywayModalOpen, setIsSaveAnywayModalOpen] = useState(false);

  const mutation = useCreateResourceLinkMutation();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      url: "",
      name: "",
    },
  });

  const [debouncedUrl] = useDebounce(form.watch("url"), 1000);

  const url = urlSchema.safeParse(debouncedUrl).success ? debouncedUrl : "";

  const {
    data: isUrlOnline,
    isError: isUrlOffline,
    isLoading: isUrlOnlineLoading,
  } = useCheckUrlStatus(url);

  const saveLink = (values: z.infer<typeof formSchema>) => {
    mutation.mutate({
      companyId: resourceLibrary?.owner.id || "",
      resourceLibraryId: resourceLibrary?.id || "",
      name: values.name.trim(),
      url: values.url.trim(),
      tags: addCurrentEditionTag ? [`editionyear::${edition?.year}`] : [],
    });
  };

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    if (isUrlOffline) {
      setIsSaveAnywayModalOpen(true);
      return;
    }

    saveLink(values);
  };

  const onSubmitForced = (values: z.infer<typeof formSchema>) => {
    saveLink(values);
    setIsSaveAnywayModalOpen(false);
  };

  const handleOpenChange = (isOpen: boolean, data?: OpenChangeData) => {
    if (isOpen) {
      onOpenChange(isOpen, null);
    } else {
      form.reset();
      mutation.reset();
      onOpenChange(false, data || null);
    }
  };

  useEffect(() => {
    if (mutation.isSuccess) {
      toast(
        <span>
          El link <strong>{mutation.variables.name}</strong> se ha registrado
          exitosamente.
        </span>,
      );

      handleOpenChange(false, mutation.data);
    }
  }, [mutation]);

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent className="flex flex-col">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
            <SheetHeader>
              <SheetTitle>Nuevo link público</SheetTitle>
              <SheetDescription className="hidden">
                Publica un link en la biblioteca de recursos
              </SheetDescription>
            </SheetHeader>

            <SheetBody>
              <div className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="url"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>Link</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Ingresa una URL válida"
                          maxLength={2048}
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />

                      {fieldState.isDirty && (
                        <>
                          {!url && !fieldState.invalid && (
                            <div className="text-sm font-medium text-destructive">
                              Ingresa una URL válida
                            </div>
                          )}

                          {isUrlOnlineLoading && (
                            <div className="inline-flex gap-2">
                              <Spinner className="m-0" />
                              <span className="pt-px text-sm text-muted-foreground">
                                Verificando la validez del enlace...
                              </span>
                            </div>
                          )}

                          {!isUrlOnlineLoading && isUrlOffline && (
                            <Alert className="border-0 bg-yellow-50">
                              <AlertCircle className="h-4 w-4" />

                              <AlertTitle>
                                El enlace parece no estar disponible
                              </AlertTitle>

                              <AlertDescription>
                                Verifica que sea de acceso público y esté
                                disponible en todo momento.
                              </AlertDescription>
                            </Alert>
                          )}

                          {!isUrlOnlineLoading && isUrlOnline && (
                            <Alert className="border-0 bg-green-50">
                              <CheckCircle className="h-4 w-4" />

                              <AlertTitle>Enlace accesible</AlertTitle>

                              <AlertDescription>
                                Asegúrate de que el enlace esté disponible al
                                momento de la evaluación
                              </AlertDescription>
                            </Alert>
                          )}

                          {url && (
                            <Button
                              className="pl-0"
                              size="sm"
                              variant="link"
                              asChild
                            >
                              <a
                                href={field.value}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <ExportSquare size={14} />
                                Abrir enlace
                              </a>
                            </Button>
                          )}
                        </>
                      )}
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nombre</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Añade un nombre al recurso"
                          minLength={3}
                          maxLength={255}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </SheetBody>

            <SheetFooter className="grid grid-cols-2 items-center">
              <Button
                type="submit"
                variant="default"
                size="lg"
                disabled={mutation.isPending || isUrlOnlineLoading}
              >
                {mutation.isPending && <Spinner className="mr-2" />}
                Guardar
              </Button>

              <Button
                type="button"
                variant="secondary"
                size="lg"
                onClick={() => handleOpenChange(false)}
              >
                Cancelar
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>

      <AlertDialog
        open={isSaveAnywayModalOpen}
        onOpenChange={setIsSaveAnywayModalOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              El link parece no estar disponible
            </AlertDialogTitle>
            <AlertDialogDescription>
              Por favor, verifica manualmente que sea válido y esté activo.
              ¿Quieres continuar de todos modos?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              className={buttonVariants({ variant: "secondary" })}
              onClick={form.handleSubmit(onSubmitForced)}
              disabled={mutation.isPending}
            >
              Guardar de todos modos
            </AlertDialogAction>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: "default" }))}
            >
              Cancelar y revisar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
};

export default LinkDrawer;
